import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  Settings,
  Plus,
  ArrowRight,
  Calendar,
  Shield,
  BarChart3,
  RefreshCw,
  Activity,
  Timer
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Loading } from '@/components/Loading';
import { slaService } from '@/services/slaService';
import type { SLADashboardData, SLAPolicy, SLABreachAlert, SLAMetricType } from '@/types/sla';
import { TicketPriority } from '@/types/sla';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * SLA Management Dashboard
 * Main overview page for SLA management system
 */
export const SLADashboard: React.FC = () => {
  useDocumentTitle('SLA Management | SupportDesk');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<SLADashboardData | null>(null);
  const [policies, setPolicies] = useState<SLAPolicy[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboardData(true);
      }, 30000); // Refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const [data, policiesData] = await Promise.all([
        slaService.getDashboardData(), slaService.getAllPolicies()
      ]);
      
      // Validate dashboard data quality
      const dataQualityIssues = validateDashboardData(data);
      if (dataQualityIssues.length > 0) {
        console.warn('Dashboard data quality issues:', dataQualityIssues);
      }
      
      setDashboardData(data);
      setPolicies(policiesData.filter(p => p.isActive));
      
    } catch (err: any) {
      const errorMessage = err?.message || 'Unknown error occurred';
      setError(`Failed to load SLA dashboard: ${errorMessage}`);
      console.error('Error loading SLA dashboard:', err);
      
      setDashboardData(null);
      setPolicies([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatTimeRemaining = (alert: SLABreachAlert): string => {
    return slaService.formatTimeRemaining(alert.timeRemaining);
  };

  const getAlertColor = (alert: SLABreachAlert): string => {
    if (alert.isBreached) {
      return 'bg-red-50 border-red-200';
    }
    switch (alert.warningLevel) {
      case 'critical':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getPriorityLabel = (priority: TicketPriority): string => {
    const labels = {
      [TicketPriority.Low]: 'Low',
      [TicketPriority.Medium]: 'Medium',
      [TicketPriority.High]: 'High',
      [TicketPriority.Urgent]: 'Urgent',
      [TicketPriority.Critical]: 'Critical'
    };
    return labels[priority] || 'Unknown';
  };

  const getPriorityColor = (priority: TicketPriority): string => {
    return slaService.getPriorityColor(priority);
  };

  /**
   * Validate dashboard data quality for testing
   */
  const validateDashboardData = (data: SLADashboardData): string[] => {
    const issues: string[] = [];
    
    // Check for negative values
    if (data.totalActivePolicies < 0) issues.push('Negative active policies count');
    if (data.totalActiveTickets < 0) issues.push('Negative active tickets count');
    if (data.ticketsAtRisk < 0) issues.push('Negative at-risk tickets count');
    if (data.ticketsBreached < 0) issues.push('Negative breached tickets count');
    
    // Check compliance rate bounds
    if (data.overallComplianceRate !== null && (data.overallComplianceRate < 0 || data.overallComplianceRate > 100)) {
      issues.push('Invalid compliance rate (must be 0-100)');
    }
    
    // Check data consistency
    if (data.breachAlerts.length !== (data.ticketsAtRisk + data.ticketsBreached)) {
      issues.push('Breach alerts count mismatch with tickets at risk + breached');
    }
    
    // Check for null/undefined critical values
    if (data.totalActivePolicies === null || data.totalActivePolicies === undefined) {
      issues.push('Missing active policies count');
    }
    
    return issues;
  };

  const getSLATierColor = (tier: string): string => {
    switch (tier) {
      case 'Enterprise':
        return 'bg-purple-100 text-purple-800';
      case 'Premium':
        return 'bg-blue-100 text-blue-800';
      case 'VIP':
        return 'bg-yellow-100 text-yellow-800';
      case 'Basic':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Dashboard</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => loadDashboardData()} variant="primary">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  // Show error state but still allow limited dashboard functionality
  if (!dashboardData && !error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="p-6 text-center">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Dashboard Data</h3>
          <p className="text-gray-600 mb-4">
            Dashboard data is not available. This may be due to no active SLA policies or configuration issues.
          </p>
          <Button onClick={() => loadDashboardData()} variant="primary">
            Retry Loading
          </Button>
        </Card>
      </div>
    );
  }

  const displayData = dashboardData!;

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Title and actions stack below sm, and the four actions wrap, so the header stays
          inside a 320px viewport rather than forcing a page-level horizontal scrollbar
          (BugHerd #147). gap replaces space-x so wrapped rows keep a gutter. */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SLA Management</h1>
          <p className="text-gray-600 mt-1">
            Monitor service level agreements, response times, and escalations
          </p>
          {dashboardData && (
            <div className="flex items-center mt-2 text-sm text-gray-500">
              <Activity className="h-4 w-4 mr-1" />
              Last updated: {new Date().toLocaleTimeString()}
              {autoRefresh && (
                <span className="ml-2 flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
                  Auto-refreshing
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            icon={refreshing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            onClick={() => loadDashboardData(true)}
            disabled={refreshing}
          >
            Refresh
          </Button>
          <Button
            variant={autoRefresh ? "outline" : "ghost"}
            icon={<Timer className="h-4 w-4" />}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            Auto-refresh
          </Button>
          <Button
            variant="outline"
            icon={<BarChart3 className="h-4 w-4" />}
            onClick={() => navigate('/reports/overview')}
          >
            View Reports
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/sla/policies')}
          >
            Manage Policies
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
            <span className="text-yellow-700">Warning: {error}</span>
            <span className="text-yellow-600 ml-2">(Showing cached/fallback data)</span>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className={`p-6 ${error ? 'bg-yellow-50 border-yellow-200' : ''}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Policies</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="active-policies-count">
                {displayData.totalActivePolicies}
              </p>
            </div>
          </div>
        </Card>

        <Card className={`p-6 ${error ? 'bg-yellow-50 border-yellow-200' : ''}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="compliance-rate">
                {displayData.overallComplianceRate === null ? 'No measurements' : `${Math.round(displayData.overallComplianceRate)}%`}
              </p>
            </div>
          </div>
        </Card>

        <Card className={`p-6 ${error ? 'bg-yellow-50 border-yellow-200' : ''}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">SLA Breaches</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="breached-tickets-count">
                {displayData.ticketsBreached}
              </p>
            </div>
          </div>
        </Card>

        <Card className={`p-6 ${error ? 'bg-yellow-50 border-yellow-200' : ''}`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">At Risk</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="at-risk-tickets-count">
                {displayData.ticketsAtRisk}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent SLA Breaches */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent SLA Breaches</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/sla/breaches')}
              icon={<ArrowRight className="h-4 w-4" />}
            >
              View All
            </Button>
          </div>
          
          <div className="space-y-3">
            {displayData.breachAlerts.filter(alert => alert.isBreached).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p>No recent SLA breaches!</p>
                {error && (
                  <p className="text-xs text-yellow-600 mt-1">Unable to load latest breach data</p>
                )}
              </div>
            ) : (
              displayData.breachAlerts
                .filter(alert => alert.isBreached)
                .slice(0, 5)
                .map((alert) => (
                <div key={alert.ticketId} className={`flex items-center justify-between p-3 rounded-lg border ${getAlertColor(alert)}`}>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Link
                        to={`/tickets/${alert.ticketId}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        #{alert.ticketNumber} - {alert.title}
                      </Link>
                      <Badge className={getPriorityColor(alert.priority)} size="sm">
                        {getPriorityLabel(alert.priority)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      {alert.assignedAgentName ? `Agent: ${alert.assignedAgentName}` : 'Unassigned'}
                      {alert.customerName && ` • Customer: ${alert.customerName}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-red-600">Breached</p>
                    <p className="text-xs text-gray-500">
                      {formatTimeRemaining(alert)} overdue
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Tickets Approaching Breach */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Approaching Breach</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/sla/breaches')}
              icon={<ArrowRight className="h-4 w-4" />}
            >
              View All
            </Button>
          </div>
          
          <div className="space-y-3">
            {displayData.breachAlerts.filter(alert => !alert.isBreached).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p>No tickets at risk!</p>
                {error && (
                  <p className="text-xs text-yellow-600 mt-1">Unable to load latest at-risk data</p>
                )}
              </div>
            ) : (
              displayData.breachAlerts
                .filter(alert => !alert.isBreached)
                .slice(0, 5)
                .map((alert) => (
                <div key={alert.ticketId} className={`flex items-center justify-between p-3 rounded-lg border ${getAlertColor(alert)}`}>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Link
                        to={`/tickets/${alert.ticketId}`}
                        className="font-medium text-gray-900 hover:text-primary-600"
                      >
                        #{alert.ticketNumber} - {alert.title}
                      </Link>
                      <Badge className={getPriorityColor(alert.priority)} size="sm">
                        {getPriorityLabel(alert.priority)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">
                      {alert.assignedAgentName ? `Agent: ${alert.assignedAgentName}` : 'Unassigned'}
                      {alert.customerName && ` • Customer: ${alert.customerName}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${alert.warningLevel === 'critical' ? 'text-red-600' : 'text-yellow-600'}`}>
                      {formatTimeRemaining(alert)}
                    </p>
                    <p className="text-xs text-gray-500">remaining</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      {/* Active SLA Policies */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Active SLA Policies</h3>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/sla/policies')}
            icon={<ArrowRight className="h-4 w-4" />}
          >
            Manage Policies
          </Button>
        </div>
        
        {policies.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Active SLA Policies</h4>
            <p className="text-gray-600 mb-4">Create your first SLA policy to get started with service level management.</p>
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/sla/policies')}
            >
              Create SLA Policy
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {policies.slice(0, 6).map((policy) => (
              <div key={policy.id} className="p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{policy.name}</h4>
                    {policy.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{policy.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {policy.isDefault && (
                      <Badge variant="success" size="sm">Default</Badge>
                    )}
                    <Badge className={getSLATierColor(slaService.getSLATierName(policy.tier))}>
                      {slaService.getSLATierName(policy.tier)}
                    </Badge>
                  </div>
                </div>
                
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>First Response:</span>
                    <span className="font-medium">
                      {slaService.minutesToHoursMinutes(policy.firstResponseCriticalPriorityMinutes)} - {slaService.minutesToHoursMinutes(policy.firstResponseLowPriorityMinutes)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>Resolution:</span>
                    <span className="font-medium">
                      {slaService.minutesToHoursMinutes(policy.resolutionCriticalPriorityMinutes)} - {slaService.minutesToHoursMinutes(policy.resolutionLowPriorityMinutes)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>Business Hours Only:</span>
                    <span className={policy.businessHoursOnly ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                      {policy.businessHoursOnly ? 'Yes' : '24/7'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/sla/policies')}
                    icon={<Settings className="h-3 w-3" />}
                  >
                    Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quick Actions */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Button
            variant="outline"
            className="justify-start h-auto py-3"
            onClick={() => navigate('/sla/policies')}
            icon={<Shield className="h-5 w-5" />}
          >
            <div className="text-left">
              <div className="font-medium">SLA Policies</div>
              <div className="text-xs text-gray-500 mt-1">Create and manage policies</div>
            </div>
          </Button>
          
          
          <Button
            variant="outline"
            className="justify-start h-auto py-3"
            onClick={() => navigate('/sla/business-hours')}
            icon={<Calendar className="h-5 w-5" />}
          >
            <div className="text-left">
              <div className="font-medium">Business Hours</div>
              <div className="text-xs text-gray-500 mt-1">Set operating schedules</div>
            </div>
          </Button>
          
          <Button
            variant="outline"
            className="justify-start h-auto py-3"
            onClick={() => navigate('/reports/overview')}
            icon={<BarChart3 className="h-5 w-5" />}
          >
            <div className="text-left">
              <div className="font-medium">SLA Reports</div>
              <div className="text-xs text-gray-500 mt-1">View performance analytics</div>
            </div>
          </Button>
        </div>
        
        {/* Additional Controls */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          {/* Same wrapping treatment as the page header: at 320px the Auto-refresh button
              was pushed to a right edge of 325px and clipped, and the status metadata
              collided with it (BugHerd #147). */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="text-sm text-gray-600" data-testid="dashboard-status-summary">
                Monitoring {displayData.totalActiveTickets} active tickets
                {/* The service swallows source failures into substituted values, so the
                    page-level error alone never fired here. isPartialData reports it. */}
                {(error || displayData.isPartialData) && (
                  <span className="text-yellow-600 ml-1" data-testid="partial-data-notice">
                    (some figures unavailable - showing partial data)
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600">
                •
              </div>
              <div className="text-sm text-gray-600">
                {displayData.totalActivePolicies} active {displayData.totalActivePolicies === 1 ? 'policy' : 'policies'}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
                icon={<Timer className="h-4 w-4" />}
              >
                {autoRefresh ? 'Disable' : 'Enable'} Auto-refresh
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};